/**
 * Small canvas plotting layer.
 *
 * Not a chart library - just enough to draw the three plots that matter, at
 * device pixel density, with axes you can actually read a number off.
 */

export interface Series {
  x: ArrayLike<number>;
  y: ArrayLike<number>;
  color: string;
  width?: number;
  /** Fill down to the bottom of the plot. */
  fill?: string;
}

export interface Marker {
  x: number;
  y?: number;
  label: string;
  color: string;
}

export interface GuideLine {
  from: [number, number];
  to: [number, number];
  color: string;
  dash?: number[];
  label?: string;
}

export interface Shade {
  from: number;
  to: number;
  color: string;
  label?: string;
}

export interface PlotOptions {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  xLabel?: string;
  yLabel?: string;
  xTicks?: number[];
  yTicks?: number[];
  formatX?: (value: number) => string;
  formatY?: (value: number) => string;
  markers?: Marker[];
  guides?: GuideLine[];
  shades?: Shade[];
  empty?: string;
}

const PAD = { top: 14, right: 16, bottom: 34, left: 52 };
const LABEL_FONT =
  '10.5px "Helvetica Neue", Helvetica, Inter, "Segoe UI", Arial, sans-serif';

export interface PlotTheme {
  ink: string;
  accent: string;
  grid: string;
  axis: string;
  text: string;
  faint: string;
  fill: string;
}

/**
 * Palette lifted straight from the stylesheet's custom properties.
 *
 * The plots are part of the same printed page as the type around them, so they
 * read their colours from it rather than keeping a second set that drifts -
 * which also means the dark-scheme rules apply to the canvases for free.
 */
export function plotTheme(): PlotTheme {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string): string =>
    style.getPropertyValue(name).trim() || fallback;
  return {
    ink: read('--ink', '#111111'),
    accent: read('--accent', '#e3000f'),
    grid: read('--grid-line', 'rgba(17, 17, 17, 0.1)'),
    axis: read('--rule', 'rgba(17, 17, 17, 0.9)'),
    text: read('--ink-muted', 'rgba(17, 17, 17, 0.58)'),
    faint: read('--ink-faint', 'rgba(17, 17, 17, 0.38)'),
    fill: read('--plot-fill', 'rgba(17, 17, 17, 0.08)'),
  };
}

/** Size the backing store to the element's real pixel dimensions. */
function prepare(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  const ratio = window.devicePixelRatio || 1;
  const width = canvas.clientWidth || 600;
  const height = canvas.clientHeight || 200;
  if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
  }
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);
  return context;
}

export function drawPlot(canvas: HTMLCanvasElement, series: Series[], options: PlotOptions): void {
  const context = prepare(canvas);
  if (!context) return;
  const { grid: GRID, axis: AXIS, text: TEXT } = plotTheme();

  const width = canvas.clientWidth || 600;
  const height = canvas.clientHeight || 200;
  const plotWidth = Math.max(1, width - PAD.left - PAD.right);
  const plotHeight = Math.max(1, height - PAD.top - PAD.bottom);

  const { xMin, xMax, yMin, yMax } = options;
  const toX = (value: number): number => PAD.left + ((value - xMin) / (xMax - xMin)) * plotWidth;
  const toY = (value: number): number => PAD.top + (1 - (value - yMin) / (yMax - yMin)) * plotHeight;

  const formatX = options.formatX ?? ((v: number) => String(v));
  const formatY = options.formatY ?? ((v: number) => String(v));
  const xTicks = options.xTicks ?? niceTicks(xMin, xMax, 6);
  const yTicks = options.yTicks ?? niceTicks(yMin, yMax, 5);

  context.save();
  context.font = LABEL_FONT;
  context.textBaseline = 'middle';

  for (const shade of options.shades ?? []) {
    const from = Math.max(toX(shade.from), PAD.left);
    const to = Math.min(toX(shade.to), PAD.left + plotWidth);
    if (to <= from) continue;
    context.fillStyle = shade.color;
    context.fillRect(from, PAD.top, to - from, plotHeight);
    if (shade.label) {
      context.fillStyle = TEXT;
      context.textAlign = 'left';
      context.fillText(shade.label, from + 4, PAD.top + 10);
    }
  }

  context.strokeStyle = GRID;
  context.lineWidth = 1;
  context.textAlign = 'center';
  for (const tick of xTicks) {
    const x = Math.round(toX(tick)) + 0.5;
    if (x < PAD.left - 1 || x > PAD.left + plotWidth + 1) continue;
    context.beginPath();
    context.moveTo(x, PAD.top);
    context.lineTo(x, PAD.top + plotHeight);
    context.stroke();
    context.fillStyle = TEXT;
    context.fillText(formatX(tick), x, PAD.top + plotHeight + 13);
  }

  context.textAlign = 'right';
  for (const tick of yTicks) {
    const y = Math.round(toY(tick)) + 0.5;
    if (y < PAD.top - 1 || y > PAD.top + plotHeight + 1) continue;
    context.beginPath();
    context.moveTo(PAD.left, y);
    context.lineTo(PAD.left + plotWidth, y);
    context.stroke();
    context.fillStyle = TEXT;
    context.fillText(formatY(tick), PAD.left - 6, y);
  }

  context.save();
  context.beginPath();
  context.rect(PAD.left, PAD.top, plotWidth, plotHeight);
  context.clip();

  for (const line of series) {
    const count = Math.min(line.x.length, line.y.length);
    if (count === 0) continue;

    // More samples than pixels: draw min/max per column so a single-sample
    // echo cannot vanish between two screen columns.
    const perPixel = count / plotWidth;
    if (perPixel > 2) {
      drawDecimated(context, line, count, plotWidth, toX, toY, yMin);
    } else {
      drawPath(context, line, count, toX, toY, yMin, PAD.top + plotHeight);
    }
  }

  for (const guide of options.guides ?? []) {
    context.save();
    context.strokeStyle = guide.color;
    context.lineWidth = 1.5;
    if (guide.dash) context.setLineDash(guide.dash);
    context.beginPath();
    context.moveTo(toX(guide.from[0]), toY(guide.from[1]));
    context.lineTo(toX(guide.to[0]), toY(guide.to[1]));
    context.stroke();
    context.restore();
  }

  for (const marker of options.markers ?? []) {
    const x = toX(marker.x);
    context.strokeStyle = marker.color;
    context.lineWidth = 1;
    context.setLineDash([2, 3]);
    context.beginPath();
    context.moveTo(x, PAD.top);
    context.lineTo(x, PAD.top + plotHeight);
    context.stroke();
    context.setLineDash([]);

    if (marker.y !== undefined) {
      context.fillStyle = marker.color;
      context.beginPath();
      context.arc(x, toY(marker.y), 3, 0, Math.PI * 2);
      context.fill();
    }
    context.fillStyle = marker.color;
    context.textAlign = x > PAD.left + plotWidth - 50 ? 'right' : 'left';
    context.fillText(marker.label, x + (x > PAD.left + plotWidth - 50 ? -4 : 4), PAD.top + 8);
  }

  context.restore();

  context.strokeStyle = AXIS;
  context.lineWidth = 1;
  context.strokeRect(PAD.left + 0.5, PAD.top + 0.5, plotWidth, plotHeight);

  context.fillStyle = TEXT;
  if (options.xLabel) {
    context.textAlign = 'center';
    context.fillText(options.xLabel, PAD.left + plotWidth / 2, height - 6);
  }
  if (options.yLabel) {
    context.save();
    context.translate(12, PAD.top + plotHeight / 2);
    context.rotate(-Math.PI / 2);
    context.textAlign = 'center';
    context.fillText(options.yLabel, 0, 0);
    context.restore();
  }

  if (series.every((s) => s.x.length === 0) && options.empty) {
    context.fillStyle = TEXT;
    context.textAlign = 'center';
    context.fillText(options.empty, PAD.left + plotWidth / 2, PAD.top + plotHeight / 2);
  }

  context.restore();
}

function drawPath(
  context: CanvasRenderingContext2D,
  line: Series,
  count: number,
  toX: (v: number) => number,
  toY: (v: number) => number,
  yMin: number,
  bottom: number,
): void {
  context.beginPath();
  for (let i = 0; i < count; i++) {
    const x = toX(line.x[i]);
    const y = toY(Math.max(line.y[i], yMin));
    if (i === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  if (line.fill) {
    context.save();
    context.lineTo(toX(line.x[count - 1]), bottom);
    context.lineTo(toX(line.x[0]), bottom);
    context.closePath();
    context.fillStyle = line.fill;
    context.fill();
    context.restore();
  }
  context.strokeStyle = line.color;
  context.lineWidth = line.width ?? 1.5;
  context.stroke();
}

/** One vertical stroke per pixel column, spanning that column's min and max. */
function drawDecimated(
  context: CanvasRenderingContext2D,
  line: Series,
  count: number,
  plotWidth: number,
  toX: (v: number) => number,
  toY: (v: number) => number,
  yMin: number,
): void {
  context.strokeStyle = line.color;
  context.lineWidth = line.width ?? 1;
  context.beginPath();
  const columns = Math.ceil(plotWidth);
  const step = count / columns;
  for (let column = 0; column < columns; column++) {
    const from = Math.floor(column * step);
    const to = Math.min(count, Math.floor((column + 1) * step));
    if (to <= from) continue;
    let low = Infinity;
    let high = -Infinity;
    for (let i = from; i < to; i++) {
      const v = line.y[i];
      if (v < low) low = v;
      if (v > high) high = v;
    }
    if (!Number.isFinite(low)) continue;
    const x = Math.round(toX(line.x[from])) + 0.5;
    context.moveTo(x, toY(Math.max(low, yMin)));
    context.lineTo(x, toY(Math.max(high, yMin)));
  }
  context.stroke();
}

/** Round tick positions at 1/2/5 x 10^n spacing. */
export function niceTicks(min: number, max: number, target: number): number[] {
  if (!(max > min)) return [min];
  const raw = (max - min) / Math.max(1, target);
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalised = raw / magnitude;
  const step = (normalised >= 5 ? 5 : normalised >= 2 ? 2 : 1) * magnitude;
  const ticks: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-6; v += step) {
    ticks.push(Math.abs(v) < step * 1e-6 ? 0 : v);
  }
  return ticks;
}

export interface Bar {
  label: string;
  value: number;
  dimmed?: boolean;
}

/** Horizontal-axis bar chart, used for per-octave reverberation times. */
export function drawBars(
  canvas: HTMLCanvasElement,
  bars: Bar[],
  options: { max?: number; unit?: string; color: string; empty?: string },
): void {
  const context = prepare(canvas);
  if (!context) return;
  const { grid: GRID, axis: AXIS, text: TEXT, faint: FAINT } = plotTheme();
  const width = canvas.clientWidth || 600;
  const height = canvas.clientHeight || 160;

  context.font = LABEL_FONT;
  context.textBaseline = 'middle';

  if (bars.length === 0) {
    context.fillStyle = TEXT;
    context.textAlign = 'center';
    context.fillText(options.empty ?? 'No data', width / 2, height / 2);
    return;
  }

  const max = options.max ?? Math.max(...bars.map((b) => (Number.isFinite(b.value) ? b.value : 0))) * 1.15;
  const bottom = height - 24;
  const top = 10;
  const slot = width / bars.length;
  const barWidth = Math.min(46, slot * 0.6);

  context.strokeStyle = GRID;
  for (const tick of niceTicks(0, max, 4)) {
    const y = Math.round(bottom - (tick / max) * (bottom - top)) + 0.5;
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
    context.fillStyle = TEXT;
    context.textAlign = 'left';
    context.fillText(tick.toFixed(1), 2, y - 7);
  }

  bars.forEach((bar, index) => {
    const centre = slot * (index + 0.5);
    const value = Number.isFinite(bar.value) ? Math.min(bar.value, max) : 0;
    const barHeight = (value / max) * (bottom - top);

    context.fillStyle = bar.dimmed ? FAINT : options.color;
    context.fillRect(centre - barWidth / 2, bottom - barHeight, barWidth, barHeight);

    context.fillStyle = TEXT;
    context.textAlign = 'center';
    context.fillText(bar.label, centre, bottom + 12);
    if (Number.isFinite(bar.value)) {
      context.fillStyle = bar.dimmed ? FAINT : TEXT;
      context.fillText(bar.value.toFixed(2), centre, bottom - barHeight - 9);
    }
  });

  context.strokeStyle = AXIS;
  context.beginPath();
  context.moveTo(0, bottom + 0.5);
  context.lineTo(width, bottom + 0.5);
  context.stroke();
}
